import { useState, type ReactNode } from 'react';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import type { AgentApiJobSourcesService } from '@/types/serializers';

export type CredentialFormValues = {
  loginId: string;
  loginPass: string;
  accessUrl: string;
  basicAuthId: string;
  basicAuthPass: string;
};

type Props = {
  service: AgentApiJobSourcesService;
  serviceName: string;
  isSubmitting: boolean;
  onSubmit: (values: CredentialFormValues) => void;
  footer?: ReactNode;
};

export default function JobSourceCredentialForm({
  service,
  serviceName,
  isSubmitting,
  onSubmit,
  footer,
}: Props) {
  const [loginId, setLoginId] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [accessUrl, setAccessUrl] = useState('');
  const [basicAuthId, setBasicAuthId] = useState('');
  const [basicAuthPass, setBasicAuthPass] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showBasicAuthPassword, setShowBasicAuthPassword] = useState(false);

  const isIdPasswordUrlType =
    service.auth_type === 'ID_PASSWORD_URL' || service.auth_type === 'ID_PASSWORD_URL_BASIC_AUTH';
  const isBasicAuthType = service.auth_type === 'ID_PASSWORD_URL_BASIC_AUTH';

  const isInvalid =
    !loginId ||
    !loginPass ||
    (isIdPasswordUrlType && !accessUrl) ||
    (isBasicAuthType && (!basicAuthId || !basicAuthPass));

  const handleSubmit = () => {
    if (isInvalid) return;
    onSubmit({ loginId, loginPass, accessUrl, basicAuthId, basicAuthPass });
  };

  return (
    <div className="flex flex-col items-center justify-center gap-x3l">
      <div className="flex flex-col items-center gap-sm">
        <p className="text-3xl font-bold">{serviceName}のログイン情報を入力</p>
        <p className="text-base">
          {service.source_type === 'circus_agent_ra'
            ? 'ログインして自社求人を取り込むことができます。'
            : 'ログインして求人を取り込むことができます。'}
        </p>
      </div>
      <div className="flex w-[480px] flex-col gap-lg">
        {isIdPasswordUrlType && (
          <div className="flex flex-col gap-xs">
            <label htmlFor="linkedAccountLoginUrl" className="text-sm font-bold">
              ログインURL
            </label>
            <Input
              id="linkedAccountLoginUrl"
              name="linkedAccountLoginUrl"
              className="h-xxl w-full rounded border border-border bg-white px-3 text-sm focus:border-primary focus:outline-hidden"
              type="url"
              value={accessUrl}
              onChange={(e) => setAccessUrl(e.target.value)}
              placeholder="https://"
            />
          </div>
        )}
        <div className="flex flex-col gap-xs">
          <label htmlFor="linkedAccountLoginId" className="text-sm font-bold">
            ログインID
          </label>
          <Input
            id="linkedAccountLoginId"
            name="linkedAccountLoginId"
            className="h-xxl w-full rounded border border-border bg-white px-3 text-sm focus:border-primary focus:outline-hidden"
            type="text"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            autoComplete="one-time-code"
          />
        </div>
        <div className="flex flex-col gap-xs">
          <label htmlFor="linkedAccountLoginPassword" className="text-sm font-bold">
            ログインパスワード
          </label>
          <div className="relative">
            <Input
              id="linkedAccountLoginPassword"
              name="linkedAccountLoginPassword"
              className="h-xxl w-full rounded border border-border bg-white px-3 pr-10 text-sm focus:border-primary focus:outline-hidden"
              type={showPassword ? 'text' : 'password'}
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="absolute top-1/2 right-3 size-6 -translate-y-1/2 cursor-pointer text-[#6D6D6D] hover:text-[#262626]"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
            >
              {showPassword ? (
                <MdVisibility className="size-6" />
              ) : (
                <MdVisibilityOff className="size-6" />
              )}
            </button>
          </div>
        </div>
        {isBasicAuthType && (
          <>
            <div className="mt-md border-t border-border pt-md text-sm font-bold text-[#6D6D6D]">
              ベーシック認証
            </div>
            <div className="flex flex-col gap-xs">
              <label htmlFor="linkedAccountBasicAuthId" className="text-sm font-bold">
                ID
              </label>
              <Input
                id="linkedAccountBasicAuthId"
                name="linkedAccountBasicAuthId"
                className="h-xxl w-full rounded border border-border bg-white px-3 text-sm focus:border-primary focus:outline-hidden"
                type="text"
                value={basicAuthId}
                onChange={(e) => setBasicAuthId(e.target.value)}
                autoComplete="one-time-code"
              />
            </div>
            <div className="flex flex-col gap-xs">
              <label htmlFor="linkedAccountBasicAuthPassword" className="text-sm font-bold">
                パスワード
              </label>
              <div className="relative">
                <Input
                  id="linkedAccountBasicAuthPassword"
                  name="linkedAccountBasicAuthPassword"
                  className="h-xxl w-full rounded border border-border bg-white px-3 pr-10 text-sm focus:border-primary focus:outline-hidden"
                  type={showBasicAuthPassword ? 'text' : 'password'}
                  value={basicAuthPass}
                  onChange={(e) => setBasicAuthPass(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute top-1/2 right-3 size-6 -translate-y-1/2 cursor-pointer text-[#6D6D6D] hover:text-[#262626]"
                  onClick={() => setShowBasicAuthPassword(!showBasicAuthPassword)}
                  aria-label={
                    showBasicAuthPassword
                      ? 'ベーシック認証パスワードを非表示'
                      : 'ベーシック認証パスワードを表示'
                  }
                >
                  {showBasicAuthPassword ? (
                    <MdVisibility className="size-6" />
                  ) : (
                    <MdVisibilityOff className="size-6" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="flex w-full flex-col items-center gap-md">
        <Button
          type="button"
          className="h-[52px] w-[96px]"
          onClick={handleSubmit}
          disabled={isSubmitting || isInvalid}
        >
          ログイン
        </Button>
        {footer}
      </div>
    </div>
  );
}
